import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AccountMenu from './AccountMenu';
import { getMyProfile } from '../../api/adminUsers';

/**
 * 관리자 레이아웃
 *
 * 2026-09-23 개편 — 계정 정보를 **헤더 우측 상단**으로 옮겼다.
 * 이전에는 사이드바 좌하단에 있었다. 비밀번호 변경 버튼도 거기 있었는데,
 * 관례와 반대 위치라 사용자가 "패스워드 변경 UI가 없다"고 인지할 정도로 눈에 띄지 않았다.
 *
 * 데스크톱에도 헤더가 생겼다(이전에는 모바일에만 있었다).
 */
const AdminLayout = ({ children }) => {
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [role, setRole] = useState(null);

    // 사용자 관리 메뉴는 owner 에게만 보인다.
    // 서버도 OwnerGuard 로 막지만, 눌러서 403 을 보게 하는 것은 나쁜 안내다.
    useEffect(() => {
        let alive = true;
        getMyProfile()
            .then((me) => { if (alive) setRole(me.role); })
            .catch(() => { /* 프로필을 못 읽어도 레이아웃은 떠야 한다 */ });
        return () => { alive = false; };
    }, []);

    const navItems = [
        { path: '/admin/dashboard', label: '📊 통계 및 대시보드' },
        { path: '/admin/inquiries', label: '📋 고객 요청 관리' },
        { path: '/admin/cases', label: '💼 사례 관리' },
        { path: '/admin/products', label: '📦 제품 관리' },
        { path: '/admin/solutions', label: '💡 솔루션 관리' },
        { path: '/admin/partners', label: '🤝 파트너 관리' },
        { path: '/admin/contents', label: '📝 콘텐츠 검수' },
        { path: '/admin/popups', label: '🖼️ 팝업 관리' },
        { path: '/admin/presets', label: '✉️ 메일 템플릿 관리' },
        { path: '/admin/auto-estimate', label: '⚙️ 자동견적 관리' },
        { path: '/admin/auto-estimate/analytics', label: 'ㄴ 자동견적 통계', sub: true },
        ...(role === 'owner' ? [{ path: '/admin/users', label: '👤 사용자 관리' }] : []),
    ];

    const SidebarContent = () => (
        <>
            <div className="h-16 flex items-center px-6 border-b border-gray-100">
                <Link to="/admin/dashboard" className="text-xl font-extrabold text-gray-800 tracking-tight hover:text-primary-500 transition-colors">
                    Admin CMS
                </Link>
            </div>

            <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                {navItems.map((item) => {
                    const isActive = location.pathname.startsWith(item.path);
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                                isActive
                                    ? 'bg-primary-50 text-primary-600'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            } ${item.sub ? 'ml-6 text-xs' : ''}`}
                        >
                            {item.label}
                        </Link>
                    );
                })}
            </nav>
        </>
    );

    return (
        <div className="flex h-screen bg-gray-100 font-sans overflow-hidden">
            {/* Desktop Sidebar */}
            <aside className="hidden md:flex flex-col w-64 bg-white border-r border-gray-200 h-full shadow-sm z-20">
                <SidebarContent />
            </aside>

            {/* Mobile Drawer */}
            <div className={`fixed inset-0 bg-black/50 z-40 transition-opacity md:hidden ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsMobileMenuOpen(false)}></div>
            <aside className={`fixed inset-y-0 left-0 w-64 bg-white shadow-xl z-50 transform transition-transform duration-300 md:hidden flex flex-col ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <SidebarContent />
            </aside>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col h-full overflow-hidden">
                {/* 헤더 — 데스크톱·모바일 공통. 계정 메뉴는 항상 우측 상단이다. */}
                <header className="h-14 md:h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-8 z-30 shrink-0">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setIsMobileMenuOpen(true)}
                            aria-label="메뉴 열기"
                            className="md:hidden p-2 -ml-2 text-gray-600 hover:text-primary-500 focus:outline-none"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 6h16M4 12h16m-7 6h7"></path>
                            </svg>
                        </button>
                        <span className="font-extrabold text-gray-800 leading-none md:hidden">Admin CMS</span>
                    </div>

                    <AccountMenu />
                </header>

                <main className="flex-1 overflow-y-auto p-4 md:p-8 w-full max-w-[1600px] mx-auto">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
